import DefaultAvatar from '@/assets/images/default-avatar.jpg';
import { Button } from '@/components/Button';
import { Image } from '@/components/Image';
import { ImageCropper } from '@/components/ImageCropper';
import { Text } from '@/components/Text';
import { useUserStore } from '@/stores/useUserStore';
import { base64EncodeFile } from '@/utils/fileToBase64';
import { handleError } from '@/utils/handleError';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const UpdateUserAvatar = memo(() => {
  const { t } = useTranslation();
  const { user, updateUserAvatar, deleteUserAvatar } = useUserStore();
  const [imageSrcForCrop, setImageSrcForCrop] = useState<string>('');
  const [imageCropperIsOpen, setImageCropperIsOpen] = useState<boolean>(false);

  if (!user) return null;

  const handleUpdateAvatar = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (file) {
        setImageSrcForCrop(URL.createObjectURL(file));
        setImageCropperIsOpen(true);
      }
    };
    input.click();
  };

  const handleRemoveAvatar = async () => {
    await deleteUserAvatar(user.id);
  };

  const handleCropComplete = async (croppedImage: File) => {
    setImageCropperIsOpen(false);
    try {
      const base64Image = await base64EncodeFile(croppedImage);
      await updateUserAvatar(base64Image, user.id);
    } catch (error: unknown) {
      handleError(error);
    }
  };

  return (
    <div className="w-full">
      <Text className="text-center font-medium text-sky-800 md:text-left">
        {t('updateUser:avatar.title')}
      </Text>
      <div className="mt-4 flex flex-col items-center sm:flex-row">
        <div className="relative mb-6 sm:mb-0">
          <Image
            refreshKey={user.updated_at}
            className="size-24 rounded-full border-2 border-sky-100 object-cover shadow-sm"
            src={user.avatar}
            defaultImage={DefaultAvatar}
            alt={`${user.firstname} ${user.lastname}`}
          />
        </div>
        <div className="flex w-full flex-col space-y-3 sm:ml-8 sm:w-auto sm:flex-row sm:space-x-3 sm:space-y-0">
          <Button onClick={handleUpdateAvatar}>
            {user.avatar
              ? t('updateUser:avatar.update')
              : t('updateUser:avatar.add')}
          </Button>
          {user.avatar && (
            <Button className="bg-danger" onClick={handleRemoveAvatar}>
              {t('updateUser:avatar.remove')}
            </Button>
          )}
        </div>
      </div>
      {imageCropperIsOpen && (
        <ImageCropper
          cropShape="round"
          showGrid={false}
          ratio={1}
          imageSrc={imageSrcForCrop}
          isOpen={imageCropperIsOpen}
          onClose={() => setImageCropperIsOpen(false)}
          onCropIsComplete={handleCropComplete}
        />
      )}
    </div>
  );
});
